export interface AdminDepartureDestination {
  readonly kind: "push" | "replace" | "pop" | "exit";
  readonly to?: string;
  readonly state?: unknown;
}

export interface AdminDepartureConsent {
  approve(): void;
  cancel(): void;
  /** Pause without approval; the one-shot retry recollects all current consent. */
  suspend(): (() => AdminDepartureResult) | null;
}

export interface AdminDepartureOwner {
  /** Pure predicates/readers; revision must identify the current draft version. */
  isAffected(destination: AdminDepartureDestination): boolean;
  read(): { active: boolean; busy: boolean; revision: string };
  /**
   * Approval grants consent only; it must not discard or close the draft.
   * Optional cleanup dismisses the prompt on cancellation or invalidation.
   */
  requestConsent(consent: AdminDepartureConsent): void | (() => void);
}

export type AdminDepartureResult = "pending" | "blocked" | "proceeded";

interface Registration {
  owner: AdminDepartureOwner;
}

interface Snapshot {
  registration: Registration;
  affected: boolean;
  state: ReturnType<AdminDepartureOwner["read"]> | null;
}

interface Prompt {
  decided: boolean;
  cleanup?: () => void;
  cleanupRequested: boolean;
}

interface Departure {
  destination: AdminDepartureDestination;
  proceed: () => void;
  snapshots: Snapshot[];
  approved: Set<Registration>;
  result: AdminDepartureResult;
  driving: boolean;
  prompt: Prompt | null;
}

/** Two-phase consent: nothing owns draft destruction until proceed is called. */
export function createAdminDepartureCoordinator() {
  const registrations: Registration[] = [];
  let current: Departure | null = null;
  let callingOwner = 0;
  let generation = 0;
  let requestGeneration = 0;
  let retryOwner: (() => void) | null = null;

  const call = <T>(callback: () => T): T => {
    callingOwner += 1;
    try {
      return callback();
    } finally {
      callingOwner -= 1;
    }
  };

  const cleanPrompt = (prompt: Prompt) => {
    prompt.cleanupRequested = true;
    const cleanup = prompt.cleanup;
    prompt.cleanup = undefined;
    // Detach before invoking user code, so reentrancy cannot run cleanup twice.
    if (cleanup) {
      try {
        call(cleanup);
      } catch {
        // A failed UI cleanup must never grant consent.
      }
    }
  };

  const block = (departure: Departure) => {
    departure.result = "blocked";
    if (current === departure) current = null;
    if (departure.prompt) cleanPrompt(departure.prompt);
  };

  const capture = (destination: AdminDepartureDestination): Snapshot[] =>
    registrations.map((registration) => {
      const affected = call(() => registration.owner.isAffected(destination));
      return {
        registration,
        affected,
        state: affected ? { ...call(() => registration.owner.read()) } : null,
      };
    });

  const matches = (previous: Snapshot[], latest: Snapshot[]): boolean =>
    latest.length === previous.length &&
    latest.every((snapshot, index) => {
      const before = previous[index];
      return (
        snapshot.registration === before.registration &&
        snapshot.affected === before.affected &&
        snapshot.state?.active === before.state?.active &&
        snapshot.state?.busy === before.state?.busy &&
        snapshot.state?.revision === before.state?.revision &&
        !snapshot.state?.busy
      );
    });

  const valid = (departure: Departure): boolean => {
    if (current !== departure) return false;
    try {
      const latest = capture(departure.destination);
      const unchanged = matches(departure.snapshots, latest);
      if (current === departure && unchanged) return true;
    } catch {
      // Owner failures, like stale state, require a fresh departure attempt.
    }
    block(departure);
    return false;
  };

  const drive = (departure: Departure): void => {
    if (departure.driving) return;
    departure.driving = true;
    try {
      while (valid(departure)) {
        if (departure.prompt && !departure.prompt.decided) return;
        departure.prompt = null;
        const next = [...departure.snapshots]
          .reverse()
          .find(
            ({ registration, state }) =>
              state?.active && !departure.approved.has(registration),
          );
        if (!next) {
          departure.result = "proceeded";
          current = null;
          // Keep reentrant requests blocked while the navigation callback runs.
          call(departure.proceed);
          return;
        }

        const prompt: Prompt = { decided: false, cleanupRequested: false };
        departure.prompt = prompt;
        const decide = (approved: boolean) => {
          if (current !== departure) return;
          if (prompt.decided || departure.prompt !== prompt) {
            block(departure);
            return;
          }
          prompt.decided = true;
          if (!approved || !valid(departure)) {
            block(departure);
            return;
          }
          departure.approved.add(next.registration);
          drive(departure);
        };
        try {
          const cleanup = call(() =>
            next.registration.owner.requestConsent({
              approve: () => decide(true),
              cancel: () => decide(false),
              suspend: () => {
                if (current !== departure) return null;
                if (prompt.decided || departure.prompt !== prompt) {
                  block(departure);
                  return null;
                }
                if (!valid(departure)) return null;
                prompt.decided = true;
                const suspendedRequest = requestGeneration;
                const suspendedRegistration = generation;
                retryOwner = departure.proceed;
                block(departure);
                let used = false;
                return () => {
                  if (used) return "blocked";
                  used = true;
                  if (
                    current !== null ||
                    callingOwner > 0 ||
                    requestGeneration !== suspendedRequest ||
                    generation !== suspendedRegistration ||
                    retryOwner !== departure.proceed
                  )
                    return "blocked";
                  return coordinator.request(
                    departure.destination,
                    departure.proceed,
                  );
                };
              },
            }),
          );
          if (cleanup) prompt.cleanup = cleanup;
          if (prompt.cleanupRequested) cleanPrompt(prompt);
        } catch {
          block(departure);
        }
        // The loop rechecks after synchronous consent returns, before committing.
      }
    } finally {
      departure.driving = false;
    }
  };

  const coordinator = {
    /** Pure preflight: uncertainty and busy work both require interception. */
    needsInterception(destination: AdminDepartureDestination): boolean {
      try {
        return capture(destination).some(
          ({ state }) => state?.active === true || state?.busy === true,
        );
      } catch {
        return true;
      }
    },

    /** No consent is granted; binds a future state check to these registrations. */
    captureValidity(destination: AdminDepartureDestination): () => boolean {
      const capturedDestination = Object.freeze({ ...destination });
      const capturedGeneration = generation;
      let snapshots: Snapshot[];
      try {
        snapshots = capture(capturedDestination);
      } catch {
        return () => false;
      }
      return () => {
        try {
          return (
            capturedGeneration === generation &&
            matches(snapshots, capture(capturedDestination))
          );
        } catch {
          return false;
        }
      };
    },

    /** A later departure attempt revokes an async continuation, even if cancelled. */
    captureRequestValidity(): () => boolean {
      const capturedRequest = requestGeneration;
      return () => capturedRequest === requestGeneration && current === null;
    },

    /** Cancel only a pending request or retry owned by this proceed callback. */
    cancelPending(proceed: () => void): void {
      if (retryOwner === proceed) {
        retryOwner = null;
        requestGeneration += 1;
      }
      if (current?.proceed === proceed) block(current);
    },

    register(owner: AdminDepartureOwner): () => void {
      const registration = { owner };
      registrations.push(registration);
      generation += 1;
      if (current) block(current);
      return () => {
        const index = registrations.indexOf(registration);
        if (index < 0) return;
        registrations.splice(index, 1);
        generation += 1;
        if (current) block(current);
      };
    },

    /** A competing request is blocked; the original destination stays pending. */
    request(
      destination: AdminDepartureDestination,
      proceed: () => void,
    ): AdminDepartureResult {
      requestGeneration += 1;
      retryOwner = null;
      if (callingOwner || (current && valid(current))) return "blocked";
      const departure: Departure = {
        destination: Object.freeze({ ...destination }),
        proceed,
        snapshots: [],
        approved: new Set(),
        result: "pending",
        driving: false,
        prompt: null,
      };
      current = departure;
      try {
        departure.snapshots = capture(departure.destination);
      } catch {
        block(departure);
      }
      drive(departure);
      return departure.result;
    },

    /** Reading also releases a stale prompt; no subscription or DOM is owned here. */
    get pending(): boolean {
      return current !== null && (callingOwner > 0 || valid(current));
    },
  };
  return coordinator;
}
